import { useSetState } from '@/shared/lib/customHooks';
import { showMessageError } from '@/shared/lib';
import { handleUpdatePatientDetails } from '../../lib/patientDetailsTab';
import type { PatientDetailsTabProps } from './patientDetailsTab.types';

/**
 * Coordinates the patient details tab's drawers, confirmation modal and saves for one study. Keeps
 * the open drawer, saving flag and staged payload in merged state, and exposes handlers that open and
 * close drawers, route drawer submissions to the confirmation step or straight to the save, and
 * persist a section before refreshing the study through updateStudyDetail.
 *
 * @param props - Study data and the updateStudyDetail refresh callback.
 * @returns An object carrying the tab state and setter plus the save, open, close and submit
 * handlers.
 */
export const usePatientDetailsTab = ({
  data,
  updateStudyDetail,
}: Pick<PatientDetailsTabProps, 'data' | 'updateStudyDetail'>) => {
  const [state, setState] = useSetState<any>({
    isOpenModal: '',
    isSaving: false,
    payload: null,
    isOpenConfirmModal: false,
  });

  /**
   * Persists one section of a study's patient details. Sends the payload for the given key against the study id while isSaving is set, closes the modal and refreshes the study detail on success, surfaces the error message otherwise, and always clears the saving flag, staged payload, editing key and confirmation modal.
   *
   * @param payload - Patient details fields to persist.
   * @param key - Patient details section the payload belongs to.
   * @param couldCheckReportSize - Whether the study refresh should re-check the report size.
   * @returns Resolves once the save has completed and the transient state been cleared.
   */
  const onSave = async (payload: any, key: any, couldCheckReportSize = false) => {
    setState({ isSaving: true });
    try {
      await handleUpdatePatientDetails({ key, payload, studyId: data.id });
      setState({ isOpenModal: '' });
      updateStudyDetail?.(couldCheckReportSize);
    } catch (error) {
      showMessageError({ content: error instanceof Error ? error.message : undefined });
    } finally {
      setState({
        isSaving: false,
        payload: null,
        edittingKey: '',
        isOpenConfirmModal: false,
        couldCheckReportSize: false,
      });
    }
  };

  /**
   * Opens one of the patient details drawers. Stores the requested drawer key in isOpenModal.
   *
   * @param drawer - Key of the drawer to open.
   * @returns Nothing.
   */
  const handleOpenDrawer = (drawer: any) => {
    setState({ isOpenModal: drawer });
  };

  /**
   * Closes whichever patient details drawer or modal is open. Blanks isOpenModal and resets couldCheckReportSize.
   *
   * @returns Nothing.
   */
  const handleClose = () => {
    setState({ isOpenModal: '', couldCheckReportSize: false });
  };

  /**
   * Routes a patient details drawer submission either to a confirmation step or straight to the save. Closes the modal when status is false, stages the payload and opens the confirmation modal for patient-information edits, and otherwise saves immediately through onSave.
   *
   * @param info - Submission carrying the payload, status, isEditingPatientInfo, key and couldCheckReportSize.
   * @returns Resolves once the submission has been staged or saved.
   */
  const handleClick = async (info: any) => {
    const {
      payload,
      status = false,
      isEditingPatientInfo = false,
      key,
      couldCheckReportSize,
    } = info || {};
    if (!status) {
      setState({ isOpenModal: '' });
      return;
    }
    if (isEditingPatientInfo) {
      setState({
        payload,
        isOpenConfirmModal: true,
        edittingKey: key,
        couldCheckReportSize,
      });
    } else {
      onSave(payload, key, couldCheckReportSize);
    }
  };

  return { state, setState, onSave, handleOpenDrawer, handleClose, handleClick };
};
